import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import produce from 'immer'

import { useToastContext } from '../../../../../contexts/toast'
import {
  DeliveryForecastItemSplit,
  PatchRequest,
  WebEdiDeliveryForecastDto,
} from '../../../../../data/models'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import {
  requestCompleteDeliveryForecastsById,
  useAllDeliveryForecastItems,
  useDeliveryForecastById,
} from '../../../data'
import { AcceptDeliveryForecastView } from './AcceptDeliveryForecast.view'

type Props = {
  deliveryForecastId: string
  isConfirmationActive: boolean
}

export const AcceptDeliveryForecastData: React.FC<Props> = ({
  deliveryForecastId,
  isConfirmationActive,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visible, setVisible] = useState(false)

  const { data: deliveryForecast } = useDeliveryForecastById(deliveryForecastId)

  const { data: deliveryForecastItems } =
    useAllDeliveryForecastItems(deliveryForecastId)

  const { showToast } = useToastContext()

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  const totalSplits = deliveryForecast?.Payload.Items
    ? deliveryForecast.Payload.Items.reduce((acc, item) => {
        return acc + (item.Splits ? item.Splits.length : 0)
      }, 0)
    : 0

  const prepareCompletePatchDto = (
    newdeliveryForecast: WebEdiDeliveryForecastDto,
    olddeliveryForecast: WebEdiDeliveryForecastDto,
  ): PatchRequest<WebEdiDeliveryForecastDto> => {
    const patchDtos: PatchRequest<WebEdiDeliveryForecastDto> = []

    // ConfirmationCode
    patchDtos.push({
      op: olddeliveryForecast.Payload.ConfirmationCode ? 'replace' : 'add',
      path: '/Payload/ConfirmationCode',
      value: newdeliveryForecast.Payload.ConfirmationCode,
    })

    // ConfirmationDate
    if (
      newdeliveryForecast.Payload.ConfirmationDate !==
      olddeliveryForecast.Payload.ConfirmationDate
    ) {
      patchDtos.push({
        op: olddeliveryForecast.Payload.ConfirmationDate ? 'replace' : 'add',
        path: '/Payload/ConfirmationDate',
        value: newdeliveryForecast.Payload.ConfirmationDate,
      })
    }

    return patchDtos
  }

  async function onSubmit() {
    // deactivate the buttons
    setIsRequestRunning(true)

    try {
      // close the dialog and reset
      handleClose()

      // submit form
      await submitForm()

      // navigate back to the start page
      navigate('/delivery-forecasts')

      // show success toast
      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: isConfirmationActive
          ? t(
              'deliveryForecast.title.send.dialog.send.messages.success.summary.withConfirmation',
            )
          : t(
              'deliveryForecast.title.send.dialog.send.messages.success.summary.withoutConfirmation',
            ),
        detail: isConfirmationActive
          ? t(
              'deliveryForecast.title.send.dialog.send.messages.success.detail.withConfirmation',
              {
                count: totalSplits,
                scheduleAgreementNumber:
                  deliveryForecast?.Payload.ScheduleAgreementNumber,
                currentReleaseNumber:
                  deliveryForecast?.Payload.CurrentReleaseNumber,
              },
            )
          : t(
              'deliveryForecast.title.send.dialog.send.messages.success.detail.withoutConfirmation',
              {
                count: totalSplits,
                scheduleAgreementNumber:
                  deliveryForecast?.Payload.ScheduleAgreementNumber,
                currentReleaseNumber:
                  deliveryForecast?.Payload.CurrentReleaseNumber,
              },
            ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: isConfirmationActive
          ? t(
              'deliveryForecast.title.send.dialog.send.messages.failure.summary.withConfirmation',
            )
          : t(
              'deliveryForecast.title.send.dialog.send.messages.failure.summary.withoutConfirmation',
            ),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  // submit form
  const submitForm = () => {
    //make a copy of delfor
    const newdeliveryForecast = produce(deliveryForecast!, draft => {
      draft.Payload.ConfirmationDate = new Date().toISOString() // set date to now
      const currentDate = new Date()
      draft.Payload.ConfirmationDate = currentDate.toISOString()
      // check if a split was modified or rejected
      const isSplitModified = (split: DeliveryForecastItemSplit) =>
        split.Status === 'MODIFIED' ||
        split.Status === 'REJECTED' ||
        split.Status === 'ADDED'
      draft.Payload.ConfirmationCode = deliveryForecastItems
        ?.at(0)!
        .Splits.some(isSplitModified)
        ? 'MODIFIED'
        : 'ACCEPTED'
    })
    //patch for invoice
    const response = requestCompleteDeliveryForecastsById(
      deliveryForecastId,
      prepareCompletePatchDto(newdeliveryForecast, deliveryForecast!),
    )
    return response
  }

  // handleClose
  const handleClose = () => {
    setVisible(false)
  }

  return (
    <AcceptDeliveryForecastView
      deliveryForecastId={deliveryForecastId}
      visible={visible}
      setVisible={setVisible}
      handleClose={handleClose}
      isRequestRunning={isRequestRunning}
      onSubmit={onSubmit}
      isConfirmationActive={isConfirmationActive}
    />
  )
}
